import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import gettext from '@sdk/lang/gettext';
import Text12 from '@sdk/components/ui/typography/Text12';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text16 from '@sdk/components/ui/typography/Text16';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import Spinner from '@sdk/components/ui/Spinner';
import type { ActivityLogItemType } from '@sdk/shifts/types/ActivityLogType';
import type { DayPart } from '@sdk/dayParts/types/apiTypes';
import { ID } from '@sdk/common/types';
import { useClientFlags } from '@sdk/session/ClientFlagsContext';

import CloseSvg from '@web/assets/svg/UI/close.svg';
import ActivityLogItem from '@web/shifts/ActivityLogItem/ActivityLogItem';
import { useActivityLog } from '@web/shifts/ActivityLog/activityLog.hooks';
import {
  ActivityLogContainer,
  CloseButton,
  ActivityLogList,
  ActivityLogEmptyStateContainer,
} from '@web/shifts/ActivityLog/activityLog.styles';
import EmptyStateSvg from '@web/assets/svg/images/activity-log-empty-state.svg';
import { ApplyScheduleFilters } from '@web/shifts/ActivityLog/ApplyScheduleFilters/ApplyScheduleFilters';

type ScheduleFilters = {
  dayPartIds: ID[];
  eventTypes: string[];
  groupIds: ID[];
  locationIds: ID[];
  positionIds: ID[];
  tagIds: ID[];
  userIds: ID[];
};

type Props = {
  open: boolean;
  close: () => void;
  onItemClick: (value: ActivityLogItemType) => void;
  isMySchedule: boolean;
  isMyLocations: boolean;
  isAllLocations: boolean;
  isExperimental: boolean;
  reloadAt?: Date;
  scheduleFilters: ScheduleFilters;
  dayParts: DayPart[];
  dateRange: string[];
};

const ActivityLog = ({
  open,
  close,
  onItemClick,
  isMySchedule,
  isMyLocations,
  isAllLocations,
  isExperimental,
  reloadAt,
  scheduleFilters,
  dayParts,
  dateRange,
}: Props) => {
  const autoClose = !(isMyLocations || isMySchedule || isAllLocations || isExperimental);
  if (autoClose) {
    close();
  }

  const {
    isLoading,
    activityData,
    onScroll,
    areAllPastLogsLoaded,
    isInitialLoaded,
    applyScheduleFilters,
    setApplyScheduleFilters,
  } = useActivityLog({
    open,
    isMySchedule,
    isMyLocations,
    isAllLocations,
    isExperimental,
    reloadAt,
    scheduleFilters,
    dayParts,
    dateRange,
  });

  const { updateClientFlags } = useClientFlags();

  return (
    <ActivityLogContainer open={open}>
      <Row pTop={20} pRight={20} pBottom={20} pLeft={20} alignItems="center">
        <Text12 bold uppercase themeColor={ThemeColorType.gray}>
          {gettext('Activity log')}
        </Text12>
        <CloseButton onClick={close}>
          <CloseSvg />
        </CloseButton>
      </Row>
      <Row>
        <HorizontalSeparator />
      </Row>
      <ApplyScheduleFilters
        isOpen={open}
        applyScheduleFilters={applyScheduleFilters}
        setApplyScheduleFilters={(value) => {
          setApplyScheduleFilters(value);
          updateClientFlags({ applyScheduleFiltersOnActivityLog: value });
        }}
      />
      {isLoading && !isInitialLoaded && (
        <Row mTop={20}>
          <Spinner />
        </Row>
      )}
      {!isLoading && !activityData?.length && !autoClose ? (
        <ActivityLogEmptyStateContainer>
          <EmptyStateSvg />
          <Column mTop={20}>
            <Text16 bold themeColor={ThemeColorType.primaryText}>
              {applyScheduleFilters ? gettext('No activity') : gettext('No recorded activity')}
            </Text16>
          </Column>
          <Column mTop={10} alignItems="center">
            <Text14 textAlign="center" themeColor={ThemeColorType.gray}>
              {applyScheduleFilters
                ? gettext(
                    'No activity matches the schedule filters. Toggle off the setting above or modify your filters.'
                  )
                : gettext('Track changes made to the schedule over time in this Activity Log.')}
            </Text14>
          </Column>
        </ActivityLogEmptyStateContainer>
      ) : (
        <ActivityLogList onScroll={onScroll}>
          {activityData.map((item, index) => (
            <ActivityLogItem
              key={item.id}
              item={item}
              showSeparator={index < activityData.length - 1}
              onItemClick={onItemClick}
            />
          ))}
          <Row pTop={10} pBottom={20} justifyContent="center">
            {areAllPastLogsLoaded ? (
              <Text12 themeColor={ThemeColorType.iconGray}>{gettext('No more events')}</Text12>
            ) : (
              <Spinner />
            )}
          </Row>
        </ActivityLogList>
      )}
    </ActivityLogContainer>
  );
};

export default ActivityLog;
